<template>
  <div
    class="gallery"
    v-infinite-scroll="showMore"
    infinite-scroll-disabled="isAll"
    infinite-scroll-distance="0"
  >
    <div class="row space-bottom">
      <div class="col-md-6">
        <h3>{{ parseTextintoTitle(type) }}</h3>
      </div>

      <div class="col-md-6">
        <button
          class="btn btn-info pull-right"
          @click="update"
          v-if="!inline"
          tabindex="0"
        >
          {{ allowVideo ? 'Set Media':'Set Image' }}
        </button>

        <button
          class="btn btn-default pull-right"
          style="margin-right: 1rem"
          @click.prevent="toggleDropzone"
        >
          <i class="fa fa-fw fa-upload"></i> {{ allowVideo ? 'Upload Photo/Video':'Upload Image' }}
        </button>
      </div>
    </div>

    <transition name="slideDown">
      <div v-if="!isReview" class="row space-bottom" v-show="showDropzone">
        <div class="col-md-12">
          <dropzone
            id="imageUpload"
            ref="imageUpload"
            :options="gelleryDropzoneConfig.dropzone"
            @vdropzone-success="fetch"
          >
          </dropzone>
        </div>
      </div>
      <div v-if="isReview" class="row space-bottom" v-show="showDropzone">
        <div class="col-md-12">
          <dropzone
            id="imageUpload"
            ref="imageUpload"
            :options="configs.dropzoneReview"
            @vdropzone-error="errorUpload"
            @vdropzone-success="fetch"
          >
          </dropzone>
        </div>
      </div>
    </transition>

    <div class="row space-bottom m-2" id="thumbnails" ref="thumbnails">
      <div
        class="col-md-3 img-wrapper"
        v-for="image in limited"
        :key="image.id"
        :class="imageClass(image)"
        @click.prevent="selectImage(image)"
      >
        <div class="video-thumbnail" v-if="isVideo(image.url)">
          <video controls>
            <source :src="image.url" type="video/mp4" >
          </video>
        </div>

        <div v-else class="thumbnail" :data-src="parseImage(image.url, '_thumb')"/>

        <button class="check" tabindex="0">
          <i class="fa fa-check"></i>
        </button>
      </div>
    </div>

    <div class="row" v-if="!isAll">
      <div class="col-md-12">
        <button class="btn btn-block btn-default" @click="showMore">
          Show more
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import Blazy from 'blazy';
import Dropzone from 'vue2-dropzone';
import Common from '@/mixins/Common';
import bus from '@/utils/bus';
import 'vue2-dropzone/dist/vue2Dropzone.min.css';

let belazy;
let lazyLoadInterval;

export default {
  name: 'FileGallery',

  mixins: [Common],

  data() {
    return {
      images: [],
      checked: [],
      limit: 60,
      isAll: false,
      meta: null,

      showDropzone: false,
      videoExtensions: ['.mp4', '.webm'],
      configs: {
        dropzone: {
          url: `${this.$store.state.apiBaseUrl}/galleries`,
          method: 'post',
          headers: {
            Authorization: `Bearer ${this.$store.state.token}`,
            'Cache-Control': '',
            'X-Requested-With': '',
          },
        },
        dropzoneReview: {
          url: `${this.$store.state.apiBaseUrl}/galleries/review`,
          method: 'post',
          acceptedFiles: 'image/*',
          maxFilesize: 10,
          addRemoveLinks: true,
          headers: {
            Authorization: `Bearer ${this.$store.state.token}`,
            'Cache-Control': '',
            'X-Requested-With': '',
            'X-Ma-Client': 'backend',
          },
        },
      },
    };
  },

  props: {
    selected: {
      required: false,
      default: () => [],
    },

    type: {
      type: String,
      required: true,
    },

    isReview: {
      type: Boolean,
      required: false,
      default: false,
    },

    isMultiple: {
      type: Boolean,
      required: false,
      default: false,
    },

    inline: {
      type: Boolean,
      required: false,
      default: false,
    },
    allowVideo: {
      type: Boolean,
      required: false,
      default: false,
    },
  },

  computed: {
    limited() {
      let output = this.images;
      if (this.images.length > 0) {
        output = this.images.slice(0, this.limit);
      }

      if (!this.allowVideo) {
        return output.filter(image => !this.isVideo(image.url));
      }

      return output;
    },
    gelleryDropzoneConfig() {
      const configs = this.configs;
      const videoItem = this.videoExtensions.map(ext => ext).join(',');
      if (this.allowVideo) {
        configs.dropzone.acceptedFiles = `image/*,${videoItem}`;
      }
      return configs;
    },
  },

  components: {
    Dropzone,
  },

  watch: {
    limited() {
      this.$nextTick(() => {
        this.revalidateLazyload();
      });
    },
  },

  methods: {
    fetch() {
        return new Promise((resolve, reject) => {
          this.axios.get(`/galleries?perpage=${this.limit}`)
            .then((response) => {
              const { data, meta } = response.data;

              this.meta = meta.pagination;

              if (this.isMultiple) {
                const temp = [];

                data.forEach((image) => {
                  this.checked.forEach((checked) => {
                    if (image.id === checked.id) {
                      image.selected = true;
                    }
                  });
                  temp.push(image);
                });

                this.images = temp;
              } else {
                this.images = data.map((item) => {
                  item.selected = (this.checked.id === item.id);
                  this.revalidateLazyload();
                  return item;
                });
              }

              if (this.limit >= this.meta.total) {
                this.isAll = true;
              }

              this.$nextTick(() => {
                this.revalidateLazyload();
              });

              resolve();
            })
            .catch((e) => {
              reject();
              console.log(e);
            });
        });
    },
    errorUpload() {
      this.$swal('Max Size Reached', 'Maximum Photo file size is 10mb', 'warning');
    },
    parseImage(url, size) {
      if (!url) return '';
      const imageUrl = (url.includes('https://')) ? url : `https://${url}`;
      const imageSize = size || '_thumb';
      return imageUrl.substring(0, url.lastIndexOf('.')) + imageSize + imageUrl.substring(url.lastIndexOf('.'));
    },

    imageClass(image) {
      if (this.isMultiple) {
        return (image.selected) ? 'checked' : '';
      }

      return (image.id === this.checked.id) ? 'checked' : '';
    },

    selectImage(image) {
      if (this.isMultiple) {
        this.images = this.images.map((item) => {
          if (item.id === image.id) {
            item.selected = !item.selected;
          }
          return item;
        });

        if (this.isReview && this.checked.length >= 5) {
          this.checked = this.images.filter(item => !!item.selected);
          this.checked[0].selected = !this.checked[0].selected;
          this.checked = this.checked.slice(-5);
        } else {
          this.checked = this.images.filter(item => !!item.selected);
        }
      } else {
        this.checked = image;
      }

      if (this.inline) {
        this.$emit('update-images', {
          target: this.type,
          images: this.checked,
        });
      }
    },

    toggleDropzone() {
      this.$refs.imageUpload.setOption({
        paramName: 'file',
        uploadMultiple: false,
        headers: {
          Authorization: `Bearer ${this.$store.state.token}`,
        },
      });
      this.showDropzone = !this.showDropzone;
    },

    update() {
      this.$emit('update-images', {
        target: this.type,
        images: this.checked,
      });
      this.$store.commit('SET_MODAL');
      bus.$emit('close-modal');
    },

    showMore() {
      if (this.meta.total >= this.limit) {
        this.limit += this.limit;
        this.fetch();
      }

      if (this.limit >= this.meta.total) {
        this.isAll = true;
      }
    },

    lazyLoad() {
      // eslint-disable-next-line no-new, no-unused-vars
      belazy = new Blazy({
        selector: '.thumbnail',
      });

      lazyLoadInterval = setInterval(() => {
        this.revalidateLazyload();
      }, 2 * 1000);
    },

    revalidateLazyload() {
      if (typeof belazy !== 'undefined') {
        belazy.revalidate();
      }
    },
  },

  created() {
    if (this.selected) {
      this.checked = this.selected;
    }

    this.fetch();
  },

  mounted() {
    this.lazyLoad();
  },

  beforeDestroy() {
    clearInterval(lazyLoadInterval);
  },
};
</script>

<style lang="scss" scoped>
.gallery {
  max-height: 100%;
  overflow: hidden;
  overflow-y: auto;

  &::-webkit-scrollbar {
    width: 6px;
    height: 6px;
    background-color: #f1f1f1;
  }

  &::-webkit-scrollbar-thumb {
    background-color: #ccc;
  }

  &::-webkit-scrollbar-thumb:hover {
    background-color: #666;
  }

  h3 {
    margin: 0;
  }

  .space-bottom {
    margin-bottom: 2rem;
  }
}

#thumbnails {
  overflow-y: scroll;
  max-height: 400px;
  // padding-bottom: 200px;
}

.img-wrapper {
  position: relative;
  display: inline-block;
  margin: 0.25rem 0.5rem;
  padding: 4px;
  width: calc(100% / 6 - 1rem);
  border: 1px solid #ccc;
  background-color: #fff;
  border-radius: 4px;
  cursor: pointer;
  transition: all 0.25s ease;

  @media (max-width: 1280px) {
    width: calc(100% / 5 - 1rem);
  }

  @media (max-width: 1024px) {
    width: calc(100% / 4 - 1rem);
  }

  @media (max-width: 768px) {
    width: calc(100% / 3 - 1rem);
  }

  @media (max-width: 480px) {
    width: calc(100% / 2 - 1rem);
  }

  &::before {
    content: "";
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    margin: auto;
    height: 100%;
    width: 100%;
    background: transparent url("/static/img/loading.gif") center no-repeat;
    background-size: 100%;
    opacity: 1;
  }

  &::after {
    content: "";
    display: block;
    padding-bottom: 100%;
  }

  &:hover {
    border: 1px solid #3c8dbc;
    background-color: #ebebeb;
  }

  &.checked {
    background-color: orange;
    border: 1px solid orange;

    button.check {
      display: inline-block;
    }
  }

  .video-thumbnail{
    position: absolute;
    width: 100%;
    height: 100px;
    z-index: 2;

    video {
      width: calc(100% - 8px);
      height: 100%;
      object-fit: cover; /* 🔥 important */
    }

    &::after {
      content: "";
      display: block;
      padding-bottom: 100%;
    }

    &.b-loaded {
      opacity: 1;
    }
  }

  .thumbnail {
    position: absolute;
    display: block;
    margin: 0;
    padding: 0;
    width: calc(100% - 8px);
    border: none;
    border-radius: 0;
    background-color: transparent;
    background-position: center;
    background-repeat: no-repeat;
    background-size: cover;
    z-index: 2;
    transition: opacity 0.3s ease-in-out;
    max-width: 100%;
    opacity: 0;

    &::after {
      content: "";
      display: block;
      padding-bottom: 100%;
    }

    &.b-loaded {
      opacity: 1;
    }

    &.b-error {
      background-image: url("/static/img/attention.png");
      background-color: white;
      background-size: 50%;
      opacity: 1;
    }
  }

  button.check {
    position: absolute;
    display: none;
    padding: 0 0.3rem;
    top: -0.5rem;
    right: -0.5rem;
    background-color: #00cfbc;
    color: #fff;
    border: none;
    border-radius: 50%;
    z-index: 2;
  }
}

.m-2 {
  margin: 2px;
}
</style>



// WEBPACK FOOTER //
// src/components/modules/common/FileGallery.vue